"use client";
import React from "react";
import { generateTokenUrl } from '@/utils/tokenUrls';
// import { formatPrice } from '@/utils/formatPrice';

interface SingleTokenProductSchemaMarkupProps {
  t: any;
  ti: any;
  symbol?: string;
  imageUri?: string;
}

export default function SingleTokenProductSchemaMarkup({ t, ti, symbol, imageUri }: SingleTokenProductSchemaMarkupProps) {
  // Helper to generate absolute token URL
  function getAbsoluteTokenUrl(token: any) {
    return `https://www.solflare.com${generateTokenUrl(token)}`;
  }

  // Helper functions for safe property access
  const display = (val: unknown): string =>
    val === undefined || val === null || val === '' ? 'MISSING' : String(val);

  const safe = (obj: unknown, key: string): string => {
    if (obj && typeof obj === 'object' && key in obj) {
      const val = (obj as Record<string, unknown>)[key];
      return display(val);
    }
    return 'MISSING';
  };

  // Check if price is available - only include schema if price exists
  const price = typeof (t as any).price === 'object' ? (t as any).price.price : undefined;
  if (price === undefined || price === null) {
    return null; // Don't render schema if no price is available
  }

  // Build additional properties array, excluding missing values
  const additionalProperties = [
    {
      '@type': 'PropertyValue',
      name: 'Contract Address',
      value: safe(t, 'mint'),
    },
    // Market Cap - only include if available
    (() => {
      const price = (t as any).price || {};
      const tiMarket = (ti as any).marketDetails || {};
      const mc = price.mc ?? (t as any).mc ?? tiMarket.market_cap;
      return mc !== undefined && mc !== null ? {
        '@type': 'PropertyValue',
        name: 'Market Cap',
        value: String(mc),
      } : null;
    })(),
    // Liquidity - only include if available
    (() => {
      const price = (t as any).price || {};
      const tiMarket = (ti as any).marketDetails || {};
      const liquidity = price.liquidity ?? (t as any).liquidity ?? tiMarket.liquidity;
      return liquidity !== undefined && liquidity !== null ? {
        '@type': 'PropertyValue',
        name: 'Liquidity',
        value: String(liquidity),
      } : null;
    })(),
    // 24h Trade Volume - only include if available
    (() => {
      const price = (t as any).price || {};
      const tiMarket = (ti as any).marketDetails || {};
      const volume24h = price.volume24h ?? (t as any).volume24h?.total ?? tiMarket.day_trading_volume;
      return volume24h !== undefined && volume24h !== null ? {
        '@type': 'PropertyValue',
        name: '24h Trade Volume',
        value: String(volume24h),
      } : null;
    })(),
    // 24h Price Change - only include if available
    (() => {
      const priceChange = (t as any)?.price?.change;
      return priceChange !== undefined && priceChange !== null ? {
        '@type': 'PropertyValue',
        name: '24h Price Change in percentage',
        value: String(priceChange),
      } : null;
    })(),
    {
      '@type': 'PropertyValue',
      name: 'Blockchain',
      value: 'Solana',
    },
  ].filter(Boolean); // Remove null/undefined values

  return (
    <script
      type="application/ld+json"
      suppressHydrationWarning
      dangerouslySetInnerHTML={{
        __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: `${display(t.name)}${symbol ? ` (${symbol})` : ''}`,
          alternateName: symbol || display(t.name),
          description: typeof (ti as any).description === 'string' ? (ti as any).description : '',
          sku: safe(t, 'mint') || '',
          category: 'Cryptocurrency',
          url: getAbsoluteTokenUrl(t),
          image: imageUri ? (imageUri.startsWith('http') ? imageUri : undefined) : '/tkn_lnd_pg_assets/logo.svg',
          offers: {
            '@type': 'Offer',
            priceCurrency: 'USD',
            price: typeof price === 'number' ? price : String(price).replace(/,/g, ''),
            priceValidUntil: new Date().toISOString().split('T')[0],
            url: getAbsoluteTokenUrl(t),
          },
          additionalProperty: additionalProperties,
        })
      }}
    />
  );
}
